import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import './Support.css';
import Seo from '../common/Seo';
import { Footer } from '../layout';

const FAQ_ITEMS = [
  {
    q: 'What is a validation?',
    a: 'Each time you run your trade through the validation pipeline counts as one validation. Your rules check risk, reward-to-risk ratio, trend alignment, market timing, and more.'
  },
  {
    q: 'How do AI-powered rules work?',
    a: 'AI rules analyze market trends, key levels, and conditions using large language models. They provide contextual assessments alongside your deterministic risk rules.'
  },
  {
    q: 'Can I create my own validation rules?',
    a: 'Yes. You can customize thresholds on built-in rules or create entirely custom AI-powered rules that match your specific trading strategy.'
  },
  {
    q: 'What instruments are supported?',
    a: 'Major and minor forex pairs are fully supported. Crypto, indices, and stocks are available as early access features with more being added regularly.'
  },
  {
    q: 'How does the Trading Journal work?',
    a: 'The journal tracks your trades with a PnL calendar heatmap, equity curve, and performance statistics. You can log trades manually or automate imports via webhooks from your broker.'
  },
  {
    q: 'What is the AI Copilot Chat?',
    a: 'The AI chat lets you ask questions about instruments, get market analysis, and even extract trade specifications from natural language. It understands your selected instrument context.'
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. We use Firebase Authentication for secure login, all API calls require bearer tokens, and data is isolated per user. We never share your trading data.'
  },
  {
    q: 'How do I report a bug or issue?',
    a: 'Email us at support@nanotradingcopilot.com with a clear summary of the issue, steps to reproduce it, and any screenshots. We typically respond within 2-3 business days.'
  }
];

const Support = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="ntb-support-container">
      <Seo
        title="Support - NanoTradingCopilot"
        description="Get help with NanoTradingCopilot. Find answers to common questions about trade validation, AI rules, the trading journal, and more."
        keywords="support, help, FAQ, NanoTradingCopilot, trade validation help"
      />

      {/* Header */}
      <header className="ntb-support-header">
        <Typography variant="h1" className="ntb-support-title">
          Support
        </Typography>
        <Typography variant="body1" className="ntb-support-subtitle">
          Find answers to common questions or get in touch with our team
        </Typography>
      </header>

      {/* Contact */}
      <section className="ntb-support-contact">
        <div className="ntb-contact-card">
          <Typography variant="h3" className="ntb-contact-title">
            Need help?
          </Typography>
          <Typography variant="body1" className="ntb-contact-text">
            For bug reports, questions, or feedback, reach out to our team.
          </Typography>
          <a href="mailto:support@nanotradingcopilot.com" className="ntb-contact-email">
            support@nanotradingcopilot.com
          </a>
          <Typography variant="body2" className="ntb-contact-note">
            We typically respond within 2-3 business days
          </Typography>
        </div>
      </section>

      {/* FAQ */}
      <section className="ntb-support-section">
        <Typography variant="h2" className="ntb-section-title">
          Frequently Asked Questions
        </Typography>
        <div className="ntb-faq-list">
          {FAQ_ITEMS.map((item, index) => (
            <div
              key={index}
              className={`ntb-faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button
                className="ntb-faq-question"
                onClick={() => toggleFaq(index)}
                aria-expanded={openIndex === index}
              >
                <span>{item.q}</span>
                <span className="ntb-faq-chevron">{openIndex === index ? '\u2212' : '+'}</span>
              </button>
              {openIndex === index && (
                <div className="ntb-faq-answer">
                  <Typography variant="body2">{item.a}</Typography>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Reporting Issues */}
      <section className="ntb-support-section">
        <Typography variant="h2" className="ntb-section-title">
          Reporting Issues
        </Typography>
        <Typography variant="body1" className="ntb-support-text">
          When contacting support, please include:
        </Typography>
        <ul className="ntb-support-list">
          <li>A clear summary of the issue</li>
          <li>Steps to reproduce the problem</li>
          <li>Any screenshots or error messages</li>
          <li>Your device, browser, and OS details</li>
        </ul>
      </section>

      {/* Disclaimer */}
      <section className="ntb-support-section ntb-support-disclaimer">
        <Typography variant="body2" className="ntb-disclaimer-text">
          NanoTradingCopilot is currently in alpha. Features may change and no formal SLA is guaranteed.
          For more details, see our{' '}
          <Link to="/terms">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </Typography>
      </section>

      <Footer />
    </div>
  );
};

export default Support;
